import React, { useEffect, useState, useRef, createElement } from 'react';
const VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/bsSsvWsf6jhH9ZGjujpmRo/hf_20260618_123138_699fe016-81ba-4302-9a90-954b98b715c4.mp4';
export function ScrollVideo({
  containerId
}: {
  containerId?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fallbackVideoRef = useRef<HTMLVideoElement>(null);
  const [framesReady, setFramesReady] = useState(false);
  const framesRef = useRef<ImageBitmap[]>([]);
  const objectUrlRef = useRef<string | null>(null);
  // Frame extraction
  useEffect(() => {
    let isCancelled = false;
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    const extractFrames = async () => {
      try {
        const response = await fetch(VIDEO_URL);
        const blob = await response.blob();
        if (isCancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        video.src = objectUrl;
        await new Promise(resolve => {
          video.onloadedmetadata = resolve;
        });
        if (isCancelled) return;
        const duration = video.duration;
        const frameCount = Math.max(30, Math.min(120, Math.round(duration * 24)));
        const frames: ImageBitmap[] = [];
        // Calculate resize dimensions (max width 1280)
        let drawWidth = video.videoWidth;
        let drawHeight = video.videoHeight;
        if (drawWidth > 1280) {
          const ratio = 1280 / drawWidth;
          drawWidth = 1280;
          drawHeight = drawHeight * ratio;
        }
        for (let i = 0; i < frameCount; i++) {
          if (isCancelled) break;
          const time = i / (frameCount - 1) * (duration - 0.05);
          video.currentTime = time;
          await new Promise(resolve => {
            video.onseeked = resolve;
          });
          if (isCancelled) break;
          const bitmap = await createImageBitmap(video, {
            resizeWidth: drawWidth,
            resizeHeight: drawHeight
          });
          frames.push(bitmap);
        }
        if (!isCancelled) {
          framesRef.current = frames;
          setFramesReady(true);
        }
      } catch (err) {
        console.error('Failed to extract frames:', err);
      }
    };
    extractFrames();
    return () => {
      isCancelled = true;
      framesRef.current.forEach(bitmap => bitmap.close());
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);
  // Animation loop
  useEffect(() => {
    let rafId: number;
    let smoothedProgress = 0;
    let lastFrameIndex = -1;
    let isSeeking = false;
    const render = () => {
      let targetProgress = 0;
      if (containerId) {
        const container = document.getElementById(containerId);
        if (container) {
          const rect = container.getBoundingClientRect();
          const scrollableDistance = rect.height - window.innerHeight;
          const scrolled = -rect.top;
          targetProgress = scrollableDistance > 0 ? Math.max(0, Math.min(1, scrolled / scrollableDistance)) : 0;
        }
      } else {
        const scrollY = window.scrollY;
        const scrollHeight = document.documentElement.scrollHeight;
        const innerHeight = window.innerHeight;
        const maxScroll = scrollHeight - innerHeight;
        targetProgress = maxScroll > 0 ? Math.max(0, Math.min(1, scrollY / maxScroll)) : 0;
      }
      smoothedProgress += (targetProgress - smoothedProgress) * 0.1;
      if (framesReady && framesRef.current.length > 0) {
        const frames = framesRef.current;
        const frameIndex = Math.min(frames.length - 1, Math.max(0, Math.round(smoothedProgress * (frames.length - 1))));
        if (frameIndex !== lastFrameIndex) {
          lastFrameIndex = frameIndex;
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          if (canvas && ctx) {
            const frame = frames[frameIndex];
            // Cover math
            const canvasRatio = canvas.width / canvas.height;
            const frameRatio = frame.width / frame.height;
            let drawWidth = canvas.width;
            let drawHeight = canvas.height;
            let offsetX = 0;
            let offsetY = 0;
            if (canvasRatio > frameRatio) {
              drawHeight = canvas.width / frameRatio;
              offsetY = (canvas.height - drawHeight) / 2;
            } else {
              drawWidth = canvas.height * frameRatio;
              offsetX = (canvas.width - drawWidth) / 2;
            }
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(frame, offsetX, offsetY, drawWidth, drawHeight);
          }
        }
      } else if (fallbackVideoRef.current && fallbackVideoRef.current.duration) {
        const video = fallbackVideoRef.current;
        const targetTime = smoothedProgress * (video.duration - 0.05);
        if (!isSeeking && Math.abs(video.currentTime - targetTime) > 0.001) {
          isSeeking = true;
          video.currentTime = targetTime;
        }
      }
      rafId = requestAnimationFrame(render);
    };
    rafId = requestAnimationFrame(render);
    const handleSeeked = () => {
      isSeeking = false;
    };
    const videoEl = fallbackVideoRef.current;
    if (videoEl) {
      videoEl.addEventListener('seeked', handleSeeked);
    }
    return () => {
      cancelAnimationFrame(rafId);
      if (videoEl) {
        videoEl.removeEventListener('seeked', handleSeeked);
      }
    };
  }, [framesReady]);
  // Canvas resize
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = canvas.clientWidth * dpr;
        canvas.height = canvas.clientHeight * dpr;
        // Force a redraw on next tick by clearing the lastFrameIndex
        // (Handled implicitly as the RAF loop will redraw if canvas is cleared,
        // but we'd need to reset lastFrameIndex to guarantee it.
        // For simplicity, it will catch up on next scroll or we can just let it be).
      }
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);
  return <div className="absolute inset-0 -z-10 bg-[#0a0a0a]" data-id="element-1715">
      {!framesReady && <video ref={fallbackVideoRef} src={VIDEO_URL} muted playsInline preload="auto" onLoadedData={e => {
      // Force the very first frame to paint. A muted, never-played video
      // that only gets `currentTime` set on scroll stays black at rest
      // (no scroll delta at the top of the page), so nudge it here.
      const v = e.currentTarget;
      if (v.currentTime < 0.01) {
        v.currentTime = 0.05;
      }
    }} className="absolute inset-0 h-full w-full object-cover" data-id="element-1716" />}
      <canvas ref={canvasRef} className={`absolute inset-0 h-full w-full ${!framesReady ? 'opacity-0' : 'opacity-100'}`} data-id="element-1717" />
      <div className="absolute inset-0 bg-black/20" data-id="element-1718" />
    </div>;
}